import React from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../hooks/useTheme";
import type { ThemeMode } from "../stores/themeStore";

const modes: { value: ThemeMode; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "light", icon: Sun },
  { value: "dark", icon: Moon },
  { value: "system", icon: Monitor },
];

interface ThemeToggleProps {
  compact?: boolean;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ compact = false }) => {
  const { t } = useTranslation("common");
  const { mode, setMode } = useTheme();
  const modeLabel = (value: ThemeMode) => t(`ui.theme.${value}`);

  if (compact) {
    const current = modes.find((item) => item.value === mode) ?? modes[0];
    const next = modes[(modes.indexOf(current) + 1) % modes.length];
    const label = t("ui.theme.switchLabel", { current: modeLabel(current.value), next: modeLabel(next.value) });
    const Icon = current.icon;

    return (
      <button
        type="button"
        onClick={() => setMode(next.value)}
        title={label}
        aria-label={label}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
      >
        <Icon className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div role="group" aria-label={t("ui.theme.groupLabel")} className="flex items-center gap-1 rounded-lg bg-bg-elevated p-1">
      {modes.map(({ value, icon: Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setMode(value)}
          title={modeLabel(value)}
          aria-label={modeLabel(value)}
          aria-pressed={mode === value}
          className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            mode === value ? "bg-bg-surface text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
};

export default ThemeToggle;
